
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { flash } from "@/lib/flash";

const LINKS = [
  {
    label: "Manifesto",
    type: "section",
    target: "manifesto",
  },
  {
    label: "Grid",
    type: "section",
    target: "socials",
  },
  {
    label: "Interface",
    type: "section",
    target: "interface",
  },
  {
    label: "Song Catalogue",
    type: "route",
    target: "/song-catalogue",
  },
  {
    label: "Merch",
    type: "route",
    target: "/comik-releaf",
  },
  {
    label: "Members",
    type: "route",
    target: "/members",
  },
];

const TWITCH_URL = "https://www.twitch.tv/doriantelive";

function Nav() {
  const [scrolled, setScrolled] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  const isHome = location.pathname === "/";

  // Add the glass effect once the page has been scrolled.
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };

    handleScroll();

    window.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Scroll to a landing-page section.
  // If we're on another page, return home first.
  const goToSection = (sectionId) => {
    if (isHome) {
      document.getElementById(sectionId)?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });

      return;
    }

    navigate("/", {
      state: {
        scrollTo: sectionId,
      },
    });
  };

  // Handle section navigation after returning to the landing page.
  useEffect(() => {
    if (!isHome) {
      return;
    }

    const sectionId = location.state?.scrollTo;

    if (!sectionId) {
      return;
    }

    const frameId = requestAnimationFrame(() => {
      document.getElementById(sectionId)?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });

      navigate("/", {
        replace: true,
        state: null,
      });
    });

    return () => {
      cancelAnimationFrame(frameId);
    };
  }, [isHome, location.state, navigate]);

  // Shared styling for navigation links and buttons.
  const linkClass =
    "whitespace-nowrap text-xs uppercase tracking-widest " +
    "text-matrix-green/70 transition-colors duration-200 " +
    "hover:text-matrix-green";

  const buttonClass =
    `${linkClass} cursor-pointer bg-transparent border-0`;

  return (
    <nav
      className={
        "fixed inset-x-0 top-0 z-50 " +
        "transition-all duration-300 " +
        (scrolled
          ? "glass border-b border-matrix-green/15"
          : "border-b border-transparent")
      }
    >
      <div className="flex w-full items-center justify-between gap-4 py-4 pl-4 pr-48 sm:pl-6 sm:pr-56 lg:pl-8 lg:pr-64">
        {/* Brand */}
        <Link
          to="/"
          id="nav-home"
          className="shrink-0 font-mono text-xl font-bold tracking-tight text-matrix-green"
          aria-label="Doriantelive Home"
        >
          DTL_
        </Link>

        {/* Main navigation */}
        <div className="hidden items-center gap-5 sm:flex lg:gap-7">
          {/* Home appears only when we're not on the landing page. */}
          {!isHome && (
            <Link
              to="/"
              className={linkClass}
            >
              Home
            </Link>
          )}

          {/* Section links appear only on the landing page. */}
          {isHome &&
            LINKS.filter((link) => link.type === "section").map(
              (link) => (
                <button
                  key={link.label}
                  type="button"
                  className={buttonClass}
                  onClick={() => goToSection(link.target)}
                >
                  {link.label}
                </button>
              )
            )}

          {/* Page links appear on every page. */}
          {LINKS.filter((link) => link.type === "route").map(
            (link) => (
              <Link
                key={link.label}
                to={link.target}
                className={linkClass}
              >
                {link.label}
              </Link>
            )
          )}

          {/* Contact retains the existing flash-message action. */}
          <button
            type="button"
            className={buttonClass}
            onClick={() =>
              flash("mail@doriantelive.com", "green")
            }
          >
            Contact
          </button>

          {/* Twitch */}
          <a
            href={TWITCH_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={
              "whitespace-nowrap rounded border " +
              "border-matrix-green/40 px-3 py-2 " +
              "text-xs uppercase tracking-widest " +
              "text-matrix-green transition-all duration-200 " +
              "hover:bg-matrix-green/10"
            }
          >
            Live
          </a>
        </div>
      </div>
    </nav>
  );
}

export { Nav };
export default Nav;